import { Link } from "react-router-dom"
import { useHead, useSeoMeta } from "@unhead/react"
import {Sparkles, Clock, ArrowRight, MapPin} from 'lucide-react'
import { images, prestationsBebe, serviceArea, travelFees } from "@/data/site"
import { jsonLdGraphScript, pageCanonical } from "@/lib/seo"

function ServicesBebe() {
  useSeoMeta({
    title: "Massage Bébé & Portage Morbihan, Plouay",
    description: "Ateliers massage bébé, portage, baby yoga et atelier réflexologie émotionnelle à Inguiniel (56240).",
    ogTitle: "Massage Bébé & Portage Morbihan, Plouay",
    ogDescription: "Ateliers massage bébé, portage, baby yoga et atelier réflexologie émotionnelle à Inguiniel (56240).",
    ogImage: images.massageBebe,
    twitterCard: "summary_large_image",
  })

  const bebePageUrl = pageCanonical("/prestations-bebe")

  useHead({
    script: [
      jsonLdGraphScript(
        prestationsBebe.map((atelier) => ({
          name: atelier.title,
          description: atelier.summary,
          url: `${bebePageUrl}#${atelier.id}`,
          image: atelier.image,
          serviceType: "Atelier bébé",
          offers: atelier.pricing,
        })),
        "jsonld-offers-bebe",
      ),
    ],
  })

  return (
    <div className="text-[#2d221b]">
      <section className="mx-auto max-w-6xl px-5 pb-8 pt-14 md:px-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#d6c4b6] bg-[#efe1d4]/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.24em] text-[#b85c38]">
          <Sparkles size={14} />
          <span>Prestations Bébé</span>
        </div>
        <h1 className="mt-4 max-w-3xl font-display text-4xl font-medium leading-tight text-[#2d221b] md:text-6xl">
          Des ateliers pour tisser le lien avec votre bébé
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#5e4b3e] md:text-lg">
          En individuel chez vous ou en petit groupe, ces ateliers d'une heure vous donnent des clés
          simples pour apaiser, porter et éveiller votre bébé en toute confiance.
        </p>
      </section>

      <section className="mx-auto max-w-6xl px-5 pb-16 md:px-8">
        <div className="grid gap-8 md:grid-cols-2">
          {prestationsBebe.map((atelier) => (
            <article
              key={atelier.id}
              id={atelier.id}
              className="group flex flex-col overflow-hidden rounded-3xl border border-[#dfd0c2] bg-[#fbf7f2] shadow-warm-xs transition-all duration-300 hover:shadow-warm-md hover:-translate-y-1"
            >
              <div className="overflow-hidden p-3 pb-0">
                <img
                  src={atelier.image}
                  alt={atelier.title}
                  loading="lazy"
                  className="h-64 w-full rounded-2xl object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
                <p className="mt-1.5 px-1 text-right text-[10px] italic text-[#8c6d58]">Photo non contractuelle</p>
              </div>
              <div className="flex flex-1 flex-col justify-between p-7 md:p-8">
                <div>
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="font-display text-2xl font-medium text-[#2d221b]">{atelier.title}</h2>
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-[#dfd0c2] bg-[#efe1d4] px-3 py-1 text-xs font-semibold text-[#8a4225]">
                      <Clock size={12} />
                      {atelier.duration}
                    </span>
                  </div>
                  <p className="mt-3 text-xs leading-relaxed text-[#685548] md:text-sm">{atelier.summary}</p>
                  {atelier.details && atelier.details.length > 0 && (
                    <div className="mt-4 border-t border-[#ede0d2] pt-3">
                      <p className="text-xs font-semibold text-[#8a4225]">{atelier.details[0]}</p>
                      <ul className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-2">
                        {atelier.details.slice(1).map((item, idx) => (
                          <li key={idx} className="flex items-center gap-1.5 text-xs text-[#685548]">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#b85c38]" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {atelier.note && (
                    <p className="mt-3 text-[11px] italic text-[#8c6d58] border-t border-[#ede0d2]/70 pt-2.5">
                      {atelier.note}
                    </p>
                  )}
                </div>

                <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[#ede0d2] pt-5">
                  <div className="flex flex-wrap gap-2">
                    {atelier.pricing.map((tarif) => (
                      <div
                        key={tarif.label}
                        className="inline-flex items-center gap-2 rounded-full border border-[#ebdcd0] bg-white px-3.5 py-1.5 text-xs shadow-warm-xs"
                      >
                        <span className="text-[#5e4b3e]">{tarif.label}</span>
                        <span className="font-display text-sm font-bold text-[#8a4225] tabular-nums">
                          {tarif.price}
                        </span>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#b85c38] to-[#9d4c2e] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-white shadow-warm-xs transition hover:shadow-warm-sm hover:scale-[1.02]"
                  >
                    <span>Réserver</span>
                    <ArrowRight size={12} />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Zone d'intervention */}
      <section className="bg-[#efe1d4] border-y border-[#dfd0c2]">
        <div className="mx-auto max-w-6xl px-5 py-16 md:px-8">
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#b85c38]">
            <MapPin size={14} />
            <span>Déplacements</span>
          </div>
          <h2 className="mt-2 font-display text-3xl font-medium text-[#2d221b] md:text-4xl">Zone d'intervention</h2>
          <p className="mt-3 max-w-2xl text-xs leading-relaxed text-[#5e4b3e] md:text-sm">
            {serviceArea}
          </p>
          <p className="mt-2 max-w-2xl text-xs text-[#8c6d58] leading-relaxed">
            {travelFees}
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-8 px-5 py-16 md:grid-cols-2 md:px-8">
        <div className="overflow-hidden rounded-3xl border border-[#dfd0c2] bg-white p-2.5 shadow-warm-xs">
          <img
            src={images.linen}
            alt="Douceur et accompagnement de bébé"
            loading="lazy"
            className="h-80 w-full rounded-2xl object-cover"
          />
        </div>
        <div className="flex flex-col justify-center">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#b85c38]">Ateliers collectifs ou privés</span>
          <h2 className="mt-2 font-display text-3xl font-medium text-[#2d221b] md:text-4xl">Un atelier sur mesure ou en groupe ?</h2>
          <p className="mt-4 text-xs leading-relaxed text-[#685548] md:text-sm">
            Les ateliers peuvent être organisés à domicile ou en atelier collectif. N'hésitez pas à me contacter
            pour connaître les prochaines dates ou organiser une séance personnalisée.
          </p>
          <Link
            to="/contact"
            className="mt-6 inline-flex w-fit items-center gap-2 rounded-full border border-[#cbbab0] bg-white/90 px-6 py-3 text-xs font-medium uppercase tracking-[0.14em] text-[#5e4b3e] shadow-sm transition hover:border-[#b85c38] hover:text-[#2d221b]"
          >
            <span>Me contacter</span>
            <ArrowRight size={13} />
          </Link>
        </div>
      </section>
    </div>
  )
}

export default ServicesBebe
